// Language: t('key', { n: 3 }) gives the text in the chosen language. The source is src/lang/da.ts.
// New language: src/lang/<xx>.ts (typed against da.ts), one line in STRINGS and LANG_NAMES, and <xx> in each world's strings.ts.
// Language choice: saved setting > ?lang=xx > 'da'.
import { da } from '../lang/da';
import { en } from '../lang/en';
import { Store } from './store';

export const STRINGS = { da, en };
export type Lang = keyof typeof STRINGS;
export type Key = keyof typeof da;
export const LANGS = Object.keys(STRINGS) as Lang[];
/** The language's name in the language itself (for the language picker in the parent panel) */
export const LANG_NAMES: Record<Lang, string> = { da: 'Dansk', en: 'English' };
/** The URL parameter for choosing language: ?lang=en */
export const LANG_PARAM = 'lang';
/** Keys looked up with a composed name (e.g. 'goal.' + tag). i18n-check counts them as used. */
export const DYNAMIC_KEYS = ['goal.'];

export const isLang = (l: unknown): l is Lang => typeof l === 'string' && (LANGS as string[]).includes(l);

let cur: Lang | null = null;
function initial(): Lang {
  const saved = Store.d && Store.d.settings && Store.d.settings.lang;
  if (isLang(saved)) return saved;
  const q = typeof location !== 'undefined' ? new URLSearchParams(location.search).get(LANG_PARAM) : null;
  return isLang(q) ? q : 'da';
}
export const getLang = (): Lang => (cur = cur || initial());

/** Text for a key. {name} in the text is replaced by params.name. */
export function t(key: Key, params?: Record<string, string | number>): string {
  let s: string = STRINGS[getLang()][key] ?? da[key] ?? key;
  if (params) s = s.replace(/\{(\w+)\}/g, (m, k) => (params[k] != null ? String(params[k]) : m));
  return s;
}

const listeners: (() => void)[] = [];
/** Called when the language changes (after index.html has been updated) */
export const onLangChange = (fn: () => void) => { listeners.push(fn); };

/** Switch language, save the choice and update index.html and listeners */
export function setLang(l: Lang) {
  if (!isLang(l)) return;
  cur = l;
  Store.d.settings.lang = l; Store.save();
  applyHtml();
  for (const fn of listeners) fn();
}

/** Set texts from data-i18n (text) and data-i18n-aria (aria-label) in index.html, and <html lang> */
export function applyHtml(root: Document | HTMLElement = document) {
  document.documentElement.lang = getLang();
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach(el => { el.textContent = t(el.dataset.i18n as Key); });
  root.querySelectorAll<HTMLElement>('[data-i18n-aria]').forEach(el => { el.setAttribute('aria-label', t(el.dataset.i18nAria as Key)); });
}

// ---------- The worlds' texts (src/worlds/<key>/strings.ts) ----------
/** One language's texts for one world: name, a title per level id and optional sound words in the theme picture (scene.ts) */
export interface WorldText<Id extends string = string, Sk extends string = string> {
  name: string;
  levels: Record<Id, { title: string }>;
  /** Sound words in the theme picture, e.g. { splash: 'PLASK!' }; fetched in scene.ts with sceneWord() */
  scene?: Record<Sk, string>;
}
export type WorldStrings = Record<Lang, WorldText>;
/** If da has sound words, the other languages must have exactly the same keys; otherwise none */
type SceneOf<Sk extends string> = [Sk] extends [never] ? { scene?: never } : { scene: Record<Sk, string> };

/**
 * The world's texts. The level ids and sound-word keys in da are the source: every other language must have name, title and sound words
 * for exactly those (tsc fails otherwise). That da has all the levels' ids is checked by npm run validate (i18n-check).
 */
export function defineWorldStrings<Id extends string, Sk extends string = never>(
  s: { da: WorldText<Id, Sk> } & { [L in Exclude<Lang, 'da'>]: Omit<WorldText<NoInfer<Id>>, 'scene'> & SceneOf<NoInfer<Sk>> },
): WorldStrings {
  return s;
}
/** The world's name in the chosen language */
export const worldName = (s: WorldStrings) => (s[getLang()] || s.da).name;
/** The level's title in the chosen language (not used in the game today, which has no text for children) */
export const levelTitle = (s: WorldStrings, id: string) => ((s[getLang()] || s.da).levels[id] || s.da.levels[id] || { title: id }).title;
/** A sound word in the world's theme picture in the chosen language (falls back to da and then the key). Called inside scene(). */
export const sceneWord = (s: WorldStrings, key: string) => (s[getLang()] || s.da).scene?.[key] ?? s.da.scene?.[key] ?? key;
